// VoiceSpectrum.Web.T2 · v12.10.10 (#1396 server heartbeat while active; #1397 wheel + inner scrolls count) · 2026-10-08
// VoiceSpectrum.Web.T2 · v12.10.9.1 (#1286 one idle clock across tabs) · 2026-10-06
// v12.10.9.1 (#1286): all tabs share ONE session, so they share one idle clock. Activity in any tab writes the time to
// localStorage (vs_t2_last_activity, at most every 5 s); every tab listens to it and re-arms. Before a tab warns or
// signs out it reads the shared time again - if another tab was used meanwhile it just re-arms. Only when EVERY tab
// has been idle does the warning show, and it then shows in each open tab.
// VoiceSpectrum.Web.T2 · v12.10.9 (Security Phase 1 – #1255/#1256/#1257) · 2026-10-06
// v12.10.9: the idle window now comes from Security › Platform (GET /api/t2/security/me → policy.idleMinutes) when that
// answers; the old /t2/settings/platform sessionTimeoutMinutes stays the fallback, then 30. The sign-out records
// reason "idle" + the minutes so the sign-in page shows L12 "You were signed out after N minutes of inactivity".
// Behaviour (warning, countdown, stay signed in) is unchanged.
// IdleLogoutGuard.tsx — v12.1.28.0 (SES1-O)
// Platform > Session enforcement, with a WARNING before logout (replaces the abrupt useIdleLogout).
// Reads organizations.session_timeout_minutes via GET /t2/settings/platform. After
// (window - sessionWarnSeconds) of inactivity it shows a countdown warning + blinks the browser tab
// title; if the user does nothing it signs out at the deadline; "Stay signed in" — OR any activity
// during the warning — dismisses the modal and resets the timer. The warn lead-time is now
// configurable via GET /t2/settings/platform → sessionWarnSeconds (fallback 120). Mount once in the
// tenant Shell.
import { useEffect, useRef, useState } from 'react';
import { markSignedOut, useAuth } from '../lib/auth';
import { useAccess } from '../lib/useAccess';
import api, { ApiError, SESSION_ENDED_EVENT } from '../lib/api';
import { readSupportVisit } from '../lib/supportVisit';   // v12.10.10 (#1391)
import { toast } from '../lib/toast';
import { addActivityListeners, useActivityHeartbeat } from '@shared-ui/ActivityHeartbeat';   // v12.10.10 (#1396/#1397)

const DEFAULT_WARN_SECONDS = 120; // fallback warn lead-time when the platform setting is unavailable
const WARN_MIN = 15;
const WARN_MAX = 600;
const ACTIVITY_KEY = 'vs_t2_last_activity';
function sharedLastActivity(): number {
  try { return Number(localStorage.getItem(ACTIVITY_KEY) ?? 0) || 0; } catch { return 0; }
}
let lastWrite = 0;
function writeSharedActivity(now: number): void {
  if (now - lastWrite < 5000) return;
  lastWrite = now;
  try { localStorage.setItem(ACTIVITY_KEY, String(now)); } catch { /* storage blocked - this tab still has its own clock */ }
}

const pingSecurityMe = () => api.get('/t2/security/me');

export default function IdleLogoutGuard() {
  const { isAuthenticated, logout } = useAuth();
  const [warning, setWarning] = useState(false);
  const [remaining, setRemaining] = useState(DEFAULT_WARN_SECONDS);
  const stayRef = useRef<() => void>(() => {});
  // v12.10.9: Security policy idle minutes (null until /security/me answers, or if it is unavailable).
  const { me } = useAccess();
  const policyMinutesRef = useRef<number | null>(null);
  const policyIdle = me?.policy?.idleMinutes;
  policyMinutesRef.current = typeof policyIdle === 'number' && policyIdle >= 1 ? policyIdle : null;

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    const minutesRef = { current: 30 };
    const warnSecondsRef = { current: DEFAULT_WARN_SECONDS }; // configurable warn lead-time
    const warningActive = { current: false };
    let idleTimer: number | undefined;
    let countdown: number | undefined;
    let blink: number | undefined;
    const origTitle = document.title;

    const stopBlink = () => { if (blink) window.clearInterval(blink); document.title = origTitle; };
    const startBlink = () => {
      if (blink) window.clearInterval(blink);
      let on = false;
      blink = window.setInterval(() => { on = !on; document.title = on ? '⚠ Signing out…' : origTitle; }, 900);
    };
    const clearTimers = () => { if (idleTimer) window.clearTimeout(idleTimer); if (countdown) window.clearInterval(countdown); };

    const idleMinutes = () => policyMinutesRef.current ?? minutesRef.current;

    const doLogout = () => {
      clearTimers(); stopBlink();
      toast('info', 'Signed out', 'You were signed out after a period of inactivity.');
      markSignedOut('idle', idleMinutes());
      logout();
    };

    const armIdle = () => {
      if (idleTimer) window.clearTimeout(idleTimer);
      const totalMs = Math.max(60, idleMinutes() * 60) * 1000;
      const warnAtMs = Math.max(1000, totalMs - warnSecondsRef.current * 1000);
      idleTimer = window.setTimeout(enterWarning, warnAtMs);
    };

    function enterWarning() {
      // v12.10.9.1 (#1286): another tab was used since this one armed - not idle yet, re-arm from that moment.
      const shared = sharedLastActivity();
      const totalMs = Math.max(60, idleMinutes() * 60) * 1000;
      const warnAtMs = Math.max(1000, totalMs - warnSecondsRef.current * 1000);
      if (shared && Date.now() - shared < warnAtMs - 1000) {
        if (idleTimer) window.clearTimeout(idleTimer);
        idleTimer = window.setTimeout(enterWarning, warnAtMs - (Date.now() - shared));
        return;
      }
      warningActive.current = true;
      setWarning(true);
      let left = warnSecondsRef.current;
      setRemaining(left);
      startBlink();
      countdown = window.setInterval(() => {
        left -= 1;
        setRemaining(left);
        if (left <= 0) doLogout();
      }, 1000);
    }

    const exitWarning = () => {
      warningActive.current = false;
      if (countdown) window.clearInterval(countdown);
      stopBlink();
      setWarning(false);
      armIdle();
    };
    stayRef.current = exitWarning;

    // Any activity keeps the session: while warning, dismiss the modal (present = stay); otherwise
    // just re-arm the idle timer.
    const onActivity = () => { writeSharedActivity(Date.now()); if (warningActive.current) exitWarning(); else armIdle(); };
    // v12.10.9.1 (#1286): activity in another tab counts here too.
    const onStorage = (e: StorageEvent) => {
      if (e.key !== ACTIVITY_KEY || !e.newValue) return;
      if (warningActive.current) exitWarning(); else armIdle();
    };
    window.addEventListener('storage', onStorage);

    api.get<{ sessionTimeoutMinutes: number; sessionWarnSeconds?: number }>('/t2/settings/platform')
      .then((d) => {
        if (cancelled) return;
        if (d && d.sessionTimeoutMinutes >= 1) minutesRef.current = d.sessionTimeoutMinutes;
        if (d && typeof d.sessionWarnSeconds === 'number' && d.sessionWarnSeconds > 0) {
          warnSecondsRef.current = Math.min(WARN_MAX, Math.max(WARN_MIN, Math.round(d.sessionWarnSeconds)));
        }
        armIdle();
      })
      .catch(() => { if (!cancelled) armIdle(); });

    // v12.10.10 (#1397): capture phase on document + wheel - a scroll INSIDE the Messages list or a transcript never
    // reached window 'scroll' (scroll does not bubble), so reading a long list counted as idle.
    const offActivity = addActivityListeners(onActivity);
    return () => {
      cancelled = true;
      clearTimers(); stopBlink();
      offActivity();
      window.removeEventListener('storage', onStorage);
    };
  }, [isAuthenticated, logout]);

  // v12.10.10 (#1396): the server counts only REQUESTS as activity. While the person is active, tell it - at most every
  // 3 minutes (or a third of a short idle window) - so the two clocks agree and an active reader is never signed out.
  const pingEvery = Math.min(3 * 60 * 1000, Math.max(30 * 1000, ((policyMinutesRef.current ?? 30) * 60 * 1000) / 3));
  useActivityHeartbeat(isAuthenticated, pingSecurityMe, pingEvery);

  // v12.10.10 (#1391): a VoiceSpectrum Support tab learns within a minute that the practice pressed Block (or the visit
  // ended) - not on its next page load. GET /api/auth/session-state never counts as activity, so this poll cannot keep an
  // idle support visit alive. A 401 there goes through the same "session ended" path as any other request.
  useEffect(() => {
    if (!isAuthenticated || !readSupportVisit()) return;
    const t = window.setInterval(() => {
      api.get('/api/auth/session-state').catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) {
          const b = (err.body ?? {}) as { reason?: string };
          try { window.dispatchEvent(new CustomEvent(SESSION_ENDED_EVENT, { detail: { reason: b.reason ?? 'revoked' } })); } catch { /* no window */ }
        }
      });
    }, 45000);
    return () => window.clearInterval(t);
  }, [isAuthenticated]);

  if (!warning) return null;
  return (
    <div className="vs-idle-scrim" role="alertdialog" aria-live="assertive" aria-label="Session timeout warning">
      <div className="vs-idle-modal">
        <h3>Still there?</h3>
        <p>For your security, you'll be signed out in <b>{remaining}s</b> due to inactivity.</p>
        <button type="button" className="vs-idle-stay" onClick={() => stayRef.current()}>Stay signed in</button>
      </div>
    </div>
  );
}
